<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <v-btn
                class="mx-2"
                fab
                x-small
                color="primary"
                @click="$router.back()"
              >
                <v-icon dark>mdi-arrow-left</v-icon>
              </v-btn>
              <h3 class="font-weight-bold">Product Variant QR Codes</h3>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                @click="downloadAllQRCodes"
                :loading="downloadingAll"
                class="mr-2"
              >
                <v-icon left>mdi-download-multiple</v-icon>
                Download All QR Codes
              </v-btn>
              <!-- <v-btn
                color="success"
                @click="printAllQRCodes"
              >
                <v-icon left>mdi-printer</v-icon>
                Print All
              </v-btn> -->
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search ... "
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), (page = 1), getData()"
                    @keydown.enter="(page = 1), getData()"
                  >
                  </v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.size"
                    :items="sizeItems"
                    clearable
                    @click:clear="(filter.size = null), (page = 1), getData()"
                    label="Size"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.color"
                    :items="colorItems"
                    clearable
                    @click:clear="(filter.color = null), (page = 1), getData()"
                    label="Color"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 400px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>

              <v-row v-else-if="product_variants.length > 0" id="qr-codes-container">
                <v-col
                  v-for="(variant, i) in product_variants"
                  :key="`variant_${i}`"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                >
                  <v-card elevation="2" rounded="lg" class="qr-card">
                    <v-card-title class="justify-center">
                      <div class="text-center">
                        <div class="text-subtitle-1 font-weight-bold">
                          {{ variant.product ? variant.product.name : 'Product' }}
                        </div>
                        <div class="text-caption">
                          {{ variant.color }} - {{ variant.size }}
                        </div>
                      </div>
                    </v-card-title>
                    <v-card-text class="text-center">
                      <div class="qr-code-wrapper">
                        <qrcode-vue
                          :text="getVariantUrl(variant.id)"
                          :size="200"
                          error-level="H"
                          :ref="`qrcode_${variant.id}`"
                        ></qrcode-vue>
                      </div>
                      <div class="mt-2">
                        <div class="text-h6 primary--text">
                          ₹{{ formatPrice(priceOf(variant).selling) }}
                        </div>
                        <div
                          class="text-caption grey--text"
                          v-if="priceOf(variant).savings > 0"
                        >
                          <span class="text-decoration-line-through">
                            ₹{{ formatPrice(priceOf(variant).mrp) }}
                          </span>
                          <span class="error--text ml-1">
                            -{{ priceOf(variant).percent }}%
                          </span>
                        </div>
                        <div
                          class="text-caption font-weight-bold mt-1 qr-code-text"
                          v-if="variant.qr_code_data"
                        >
                          {{ variant.qr_code_data }}
                        </div>
                        <div class="text-caption grey--text">
                          Scan to view details
                        </div>
                      </div>
                    </v-card-text>
                    <v-card-actions class="justify-center">
                      <v-btn
                        color="success"
                        small
                        @click="openVariantPage(variant.id)"
                        class="mr-2"
                      >
                        <v-icon left small>mdi-eye</v-icon>
                        View
                      </v-btn>
                      <v-btn
                        color="primary"
                        small
                        @click="downloadQRCode(variant)"
                        :loading="downloadingId === variant.id"
                      >
                        <v-icon left small>mdi-download</v-icon>
                        Download
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>

              <div v-else class="text-center pa-8">
                <v-icon size="64" color="grey">mdi-qrcode-scan</v-icon>
                <p class="text-h6 grey--text mt-4">No variants found</p>
              </div>

              <v-pagination
                v-if="product_variants.length > 0"
                rounded
                v-model="page"
                :length="paginationLength"
              >
              </v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import QrcodeVue from "vue-qrcode-component";
import { priceOf, downloadVariantQrLabel } from "@/utils/variantQr";

export default {
  name: "VariantQRList",
  components: {
    QrcodeVue,
  },
  data() {
    return {
      filter: {
        search_keyword: "",
        size: "",
        color: "",
        product_id: "",
      },
      product_variants: [],
      colorItems: [],
      sizeItems: [],
      page: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      downloadingId: null,
      downloadingAll: false,
    };
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
    this.getMasters();
  },
  methods: {
    priceOf,
    formatPrice(price) {
      return Number(price || 0).toLocaleString("en-IN");
    },
    async getMasters() {
      try {
        const response = await axios.get(`masters?context=colorAndsize`);
        const masters = response.data;

        masters.colors.forEach((color) => {
          this.colorItems.push({
            id: color.id,
            text: color.description,
            value: color.code,
          });
        });

        masters.sizes.forEach((size) => {
          this.sizeItems.push({
            id: size.id,
            text: size.description,
            value: size.code,
          });
        });
      } catch (error) {
        console.error("Error loading masters:", error);
      }
    },
    async getData() {
      this.isLoading = true;

      if (this.$route.params.productId) {
        this.filter.product_id = this.decryptIt(this.$route.params.productId);
      }

      const query = new URLSearchParams({
        page: this.page,
        rowsPerPage: this.rowsPerPage,
        product_id: this.filter.product_id || "",
        size: this.filter.size || "",
        color: this.filter.color || "",
        search_keyword: this.filter.search_keyword || "",
      }).toString();

      try {
        const response = await axios.get(`products_variants?${query}`);
        this.product_variants = response.data.data;
        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.error("Error loading variants:", error);
      } finally {
        this.isLoading = false;
      }
    },
    getVariantUrl(variantId) {
      
      // Encrypt the variant ID for security
      const encryptedId = this.encryptIt(variantId.toString());

      const baseUrl = window.location.origin;
      return `${baseUrl}/variant/${encryptedId}`;
    },
    async downloadQRCode(variant) {
      this.downloadingId = variant.id;

      try {
        // Get the QR code canvas element
        const qrRef = this.$refs[`qrcode_${variant.id}`];
        if (!qrRef || !qrRef[0]) {
          console.error("QR code ref not found");
          return;
        }

        const qrCanvas = qrRef[0].$el.querySelector("canvas");
        if (!qrCanvas) {
          console.error("Canvas not found");
          return;
        }

        await downloadVariantQrLabel({ qrSource: qrCanvas, variant });
      } catch (error) {
        console.error("Error downloading QR code:", error);
      } finally {
        this.downloadingId = null;
      }
    },
    async downloadAllQRCodes() {
      this.downloadingAll = true;

      try {
        // Download all QR codes one by one with a small delay
        for (const variant of this.product_variants) {
          await this.downloadQRCode(variant);
          // Small delay to prevent browser from blocking multiple downloads
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      } catch (error) {
        console.error("Error downloading all QR codes:", error);
      } finally {
        this.downloadingAll = false;
      }
    },
    printAllQRCodes() {
      window.print();
    },
    openVariantPage(variantId) {
      const variantUrl = this.getVariantUrl(variantId);
      window.open(variantUrl, '_blank');
    },
  },
};
</script>

<style scoped>
.qr-code-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: white;
  border-radius: 8px;
}

.qr-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.qr-code-text {
  font-family: "Courier New", monospace;
  letter-spacing: 0.5px;
}

@media print {
  .v-card__title,
  .v-card__actions,
  .v-pagination,
  .v-btn,
  .v-text-field {
    display: none !important;
  }

  .qr-card {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .v-card {
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }
}
</style>
